"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RemoteComponent = void 0;
const react_1 = __importStar(require("react"));
const uuid_1 = require("uuid");
const FallbackHTML_1 = require("./FallbackHTML");
const Boundary_1 = require("./Boundary");
const logger_1 = require("./logger");
const RemoteComponentContext_1 = __importDefault(require("./RemoteComponentContext"));
const utils_1 = require("./utils");
/*
  If React is externalized on the page, we want to use that version (this
  also protects again React not being defined at all - we dynamically import it).
*/
const React = typeof window !== `undefined` && window.React ? window.React : react_1.default;
const initialDataCache = new Map();
/**
 * Checks if we have the remote component's data in a cache'ed map,
 *  if not, fetch it on the document by its id.
 *    if found on document, but not in cache, add it to cache and return
 *  if not on document or cache, return an empty object.
 */
function getCachedRemoteComponentData(id) {
    if (initialDataCache.has(id)) {
        return initialDataCache.get(id);
    }
    const emptyRemoteComponentData = {
        component: ``,
        html: ``,
        initialProps: {},
        moduleUrl: ``,
        tagName: `div`
    };
    // Added this check since in some cases of SSR, document would be undefined throwing an error.
    // Reference: https://github.com/nike-internal/dotcom.web-shell/commit/6693afcc7a48f37f7aa2583c5f65b1ea7ea4bbf6
    if (typeof window === `undefined`) {
        return emptyRemoteComponentData;
    }
    const el = document.getElementById(id);
    if (!el) {
        logger_1.warn(`Unable to hydrate initial HTML. You may see empty content in your remote component while it is reloaded client-side.`);
        return emptyRemoteComponentData;
    }
    const propsEl = el.querySelector(`[type="text/x-remote-component-data"]`);
    const remoteComponentData = emptyRemoteComponentData;
    if (propsEl) {
        Object.assign(remoteComponentData, JSON.parse(propsEl.innerText));
    }
    else {
        logger_1.warn(`Unable to provide initial data for your remote component.
You may see see your content refresh client-side. Avoid this and provide initial data from SSR when possible`);
    }
    return initialDataCache
        .set(id, Object.assign(remoteComponentData, {
        html: el.innerHTML,
        tagName: el.tagName.toLowerCase()
    }))
        .get(id);
}
const RemoteComponentRaw = (_a) => {
    var _b;
    var { endpoint, wrapperTagName = `div`, FallbackComponent = FallbackHTML_1.FallbackHTML, request = { headers: {}, query: {} } } = _a, forwardedProps = __rest(_a, ["endpoint", "wrapperTagName", "FallbackComponent", "request"]);
    const remotes = React.useContext(RemoteComponentContext_1.default);
    const { query, headers } = request;
    /**
     * Remote component will be empty until the window has loaded.
     * First calls should be fetched.
     * Secondary calls, the cache should work.
     */
    const [id] = react_1.useState(() => uuid_1.v4());
    const [remoteComponentData, setRemoteComponentData] = react_1.useState(() => getCachedRemoteComponentData(id));
    const { html, tagName: container, component, initialProps: ip } = remoteComponentData;
    const isServer = typeof window === `undefined`;
    const lazyComponentRef = React.useRef(isServer ? null : (_b = window[component]) === null || _b === void 0 ? void 0 : _b.default);
    /**
     * Fetches remote component meta data used to identify the component and determine where its module url lives.
     */
    const fetchRemoteComponentData = react_1.useCallback(async () => {
        const res = await fetch(utils_1.formatRemoteUrl(endpoint, query), {
            headers: Object.assign({ accept: `application/json` }, headers)
        });
        const json = await res.json();
        const returnedRemoteComponentData = {
            component: json.component,
            moduleUrl: json.moduleUrl,
            html: json.html,
            initialProps: json.initialProps
        };
        setRemoteComponentData(returnedRemoteComponentData);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    React.useEffect(() => {
        if (typeof window[component] === `undefined`) {
            // eslint-disable-next-line no-console
            fetchRemoteComponentData().catch(console.error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchRemoteComponentData]);
    let initialProps = ip;
    if (typeof window === `undefined`)
        return null;
    const Fallback = (props) => (React.createElement(FallbackComponent, Object.assign({}, Object.assign({ container, html }, props))));
    const MemoizedFallback = React.memo(Fallback);
    const recoDataEl = document.getElementById(id);
    if (!recoDataEl && utils_1.isDataFetched(remoteComponentData)) {
        const wrapper = document.createElement(`div`);
        wrapper.id = id;
        const metaScript = document.createElement(`script`);
        metaScript.type = `text/x-remote-component-data`;
        metaScript.innerText = JSON.stringify(remoteComponentData);
        wrapper.appendChild(metaScript);
        document.body.appendChild(wrapper);
    }
    if (utils_1.isDataFetched(remoteComponentData) && !lazyComponentRef.current) {
        const recoModuleScriptId = `${remoteComponentData.component}-module-script-${id}`;
        const el = document.getElementById(recoModuleScriptId);
        if (el) {
            lazyComponentRef.current = React.lazy(async () => new Promise((resolve, reject) => {
                el.onload = () => resolve(window[component]);
                el.onerror = reject;
            }));
        }
        else {
            const script = document.createElement(`script`);
            script.async = true;
            script.src = remoteComponentData.moduleUrl;
            script.id = recoModuleScriptId;
            document.body.appendChild(script);
            lazyComponentRef.current = React.lazy(async () => new Promise((resolve, reject) => {
                script.onload = () => resolve(window[component]);
                script.onerror = reject;
            }));
        }
    }
    const finalProps = Object.assign(Object.assign({}, initialProps), forwardedProps);
    remotes[id] = {
        endpoint,
        id,
        props: Object.assign(Object.assign({ wrapperTagName }, initialProps), forwardedProps),
        request
    };
    /**
     * We require this check since lazyComponentRef.current will return undefined and error if window not initalized on first pass.
     */
    if (!utils_1.isDataFetched(remoteComponentData)) {
        return React.createElement(MemoizedFallback, null);
    }
    return (React.createElement(Boundary_1.ErrorBoundary, { endpoint: endpoint, fallback: MemoizedFallback },
        React.createElement(React.Suspense, { fallback: React.createElement(MemoizedFallback, Object.assign({}, finalProps)) },
            React.createElement(lazyComponentRef.current, Object.assign({}, finalProps)))));
};
const RemoteComponent = react_1.memo(RemoteComponentRaw);
exports.RemoteComponent = RemoteComponent;
//# sourceMappingURL=Remote.js.map